/**
 * Discovery build-your-own box — select up to 8 samples (max 3 per product).
 */
class DiscoveryBuildBox extends HTMLElement {
  constructor() {
    super();
    this.selections = new Map();
    this.boxSize = 8;
    this.maxPerProduct = 3;
  }

  connectedCallback() {
    this.boxSize = parseInt(this.dataset.boxSize, 10) || 8;
    this.maxPerProduct = parseInt(this.dataset.maxPerProduct, 10) || 3;
    this.bundleVariantId = parseInt(this.dataset.bundleVariantId, 10) || null;
    this.fragranceLimitTooltip = this.dataset.fragranceLimitTooltip || '';

    if (!this._clickHandler) {
      this._clickHandler = (event) => this.onClick(event);
      this.addEventListener('click', this._clickHandler);
    }

    if (!this._documentClickHandler) {
      this._documentClickHandler = (event) => {
        if (
          !event.target.closest('[data-discovery-plus][data-tooltip]') &&
          !event.target.closest('[data-discovery-limit-hint]')
        ) {
          this.dismissPlusTooltips();
        }
      };
      document.addEventListener('click', this._documentClickHandler);
    }

    this.render();
  }

  disconnectedCallback() {
    if (this._documentClickHandler) {
      document.removeEventListener('click', this._documentClickHandler);
    }
  }

  onClick(event) {
    const plusBtn = event.target.closest('[data-discovery-plus]');
    const minusBtn = event.target.closest('[data-discovery-minus]');
    const addBtn = event.target.closest('[data-discovery-add]');
    const viewSelected = event.target.closest('[data-discovery-view-selected]');

    if (plusBtn) {
      event.preventDefault();
      event.stopPropagation();

      if (plusBtn.getAttribute('aria-disabled') === 'true') {
        if (plusBtn.hasAttribute('data-tooltip') && !window.matchMedia('(hover: hover)').matches) {
          this.togglePlusTooltip(plusBtn);
        }
        return;
      }

      const card = plusBtn.closest('[data-discovery-card]');
      if (card) this.changeQty(card, 1);
      return;
    }

    if (minusBtn) {
      event.preventDefault();
      const card = minusBtn.closest('[data-discovery-card]');
      if (card) this.changeQty(card, -1);
      return;
    }

    if (addBtn) {
      event.preventDefault();
      this.addToCart();
      return;
    }

    if (viewSelected) {
      event.preventDefault();
      this.querySelector('.discovery-build-box__sticky')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  getCards() {
    return Array.from(this.querySelectorAll('[data-discovery-card]'));
  }

  variantId(card) {
    return card.getAttribute('data-variant-id');
  }

  getQty(variantId) {
    return this.selections.get(String(variantId)) || 0;
  }

  get totalCount() {
    let total = 0;
    this.selections.forEach((qty) => {
      total += qty;
    });
    return total;
  }

  canAddMore() {
    return this.totalCount < this.boxSize;
  }

  dismissPlusTooltips() {
    this.querySelectorAll('[data-discovery-plus].is-tooltip-visible').forEach((button) => {
      button.classList.remove('is-tooltip-visible');
    });
    this.querySelectorAll('[data-discovery-card].is-limit-hint-visible').forEach((card) => {
      card.classList.remove('is-limit-hint-visible');
    });
  }

  togglePlusTooltip(button) {
    const card = button.closest('[data-discovery-card]');
    const isTouch = !window.matchMedia('(hover: hover)').matches;

    if (isTouch && card) {
      const wasVisible = card.classList.contains('is-limit-hint-visible');
      this.dismissPlusTooltips();
      if (!wasVisible) {
        card.classList.add('is-limit-hint-visible');
      }
      return;
    }

    const wasVisible = button.classList.contains('is-tooltip-visible');
    this.dismissPlusTooltips();
    if (!wasVisible) {
      button.classList.add('is-tooltip-visible');
    }
  }

  changeQty(card, delta) {
    const variantId = this.variantId(card);
    if (!variantId) return;

    const current = this.getQty(variantId);
    let next = current + delta;

    if (next < 0) next = 0;
    if (next > this.maxPerProduct) next = this.maxPerProduct;

    if (delta > 0 && this.totalCount + (next - current) > this.boxSize) return;

    if (next === 0) {
      this.selections.delete(String(variantId));
    } else {
      this.selections.set(String(variantId), next);
    }

    this.render();
  }

  render() {
    const total = this.totalCount;
    const remaining = Math.max(this.boxSize - total, 0);
    const progress = Math.min((total / this.boxSize) * 100, 100);
    const isComplete = total === this.boxSize;

    this.querySelectorAll('[data-discovery-count]').forEach((el) => {
      el.textContent = `${total} / ${this.boxSize}`;
    });

    this.querySelectorAll('[data-discovery-progress]').forEach((el) => {
      el.style.width = `${progress}%`;
    });

    this.querySelectorAll('[data-discovery-remaining]').forEach((el) => {
      const template = el.getAttribute('data-template') || '';
      el.textContent = template.replace(/__COUNT__/g, String(remaining));
    });

    const viewSelectedEl = this.querySelector('[data-discovery-view-selected]');
    if (viewSelectedEl) {
      const template = viewSelectedEl.getAttribute('data-label-template') || '';
      viewSelectedEl.textContent = template.replace(/__COUNT__/g, String(total));
      viewSelectedEl.hidden = total === 0;
    }

    this.getCards().forEach((card) => {
      const variantId = this.variantId(card);
      const qty = this.getQty(variantId);
      const canAdd = qty < this.maxPerProduct && this.canAddMore();

      card.classList.toggle('is-selected', qty > 0);

      const qtyEl = card.querySelector('[data-discovery-qty]');
      if (qtyEl) {
        if (qty > 0) {
          qtyEl.textContent = String(qty);
          qtyEl.removeAttribute('hidden');
        } else {
          qtyEl.setAttribute('hidden', '');
        }
      }

      const plusBtn = card.querySelector('[data-discovery-plus]');
      const minusBtn = card.querySelector('[data-discovery-minus]');
      const showFragranceLimitTooltip =
        this.fragranceLimitTooltip && qty >= this.maxPerProduct;

      if (plusBtn) {
        if (canAdd) {
          plusBtn.removeAttribute('aria-disabled');
          plusBtn.classList.remove('is-tooltip-visible');
          card.classList.remove('is-limit-hint-visible');
        } else {
          plusBtn.setAttribute('aria-disabled', 'true');
        }

        if (showFragranceLimitTooltip) {
          plusBtn.setAttribute('data-tooltip', this.fragranceLimitTooltip);
        } else {
          plusBtn.removeAttribute('data-tooltip');
          plusBtn.classList.remove('is-tooltip-visible');
          card.classList.remove('is-limit-hint-visible');
        }
      }

      if (minusBtn) minusBtn.disabled = qty <= 0;
    });

    this.renderSlots();
    this.updateAddButton(isComplete);
  }

  renderSlots() {
    const slotsEl = this.querySelector('[data-discovery-slots]');
    if (!slotsEl) return;

    const items = [];

    this.getCards().forEach((card) => {
      const qty = this.getQty(this.variantId(card));
      const image = card.getAttribute('data-image') || '';
      const title = card.getAttribute('data-title') || '';

      for (let i = 0; i < qty; i += 1) {
        items.push({ image, title });
      }
    });

    slotsEl.innerHTML = '';

    for (let i = 0; i < this.boxSize; i += 1) {
      const slot = document.createElement('div');
      slot.className = 'discovery-build-box__slot';

      if (items[i]) {
        slot.classList.add('discovery-build-box__slot--filled');
        const img = document.createElement('img');
        img.src = items[i].image;
        img.alt = items[i].title;
        img.loading = 'lazy';
        slot.appendChild(img);
      } else {
        slot.classList.add('discovery-build-box__slot--empty');
      }

      slotsEl.appendChild(slot);
    }
  }

  updateAddButton(isComplete) {
    const addBtn = this.querySelector('[data-discovery-add]');
    if (!addBtn) return;

    addBtn.disabled = !isComplete;
    addBtn.setAttribute('aria-disabled', isComplete ? 'false' : 'true');
  }

  async addToCart() {
    if (this.totalCount !== this.boxSize) return;

    const addBtn = this.querySelector('[data-discovery-add]');
    if (!addBtn) return;

    const items = [];

    this.selections.forEach((quantity, variantId) => {
      if (quantity > 0) {
        items.push({ id: parseInt(variantId, 10), quantity });
      }
    });

    if (this.bundleVariantId) {
      const bundleItem = items.find((item) => item.id === this.bundleVariantId);

      if (bundleItem) {
        bundleItem.quantity += 1;
      } else {
        items.push({ id: this.bundleVariantId, quantity: 1 });
      }
    }

    if (items.length === 0) return;

    addBtn.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const root = window.Shopify?.routes?.root || '/';
    const sectionsToBundle = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (response.ok) {
        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cartContent,
            },
          })
        );
      } else {
        window.alert(responseJson.description || responseJson.message || 'Could not add to cart.');
      }
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      console.error(error);
      window.alert('Could not add to cart. Please try again.');
    } finally {
      addBtn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('discovery-build-box')) {
  customElements.define('discovery-build-box', DiscoveryBuildBox);
}
